import { defineStore } from 'pinia';
import { toast } from 'vue3-toastify';

import type {
  IAuth,
  IAuthTenants,
  IChangePassword,
  IFormatAuthTenants,
  ILogin,
  INotificationSettings,
  IResendVerification,
  IResetPassword,
  ISignup,
  ITenant,
  IUpdateAreas,
  IUser,
  Profile
} from '@/interfaces/agency/auth';
import type { IArea } from '@/interfaces/agency/common';
import { connectSocket, disconnectSocket } from '@/plugins/socket';
import router from '@/router';
import authService from '@/services/agency/auth';
import { useDocezyStore } from '@/stores/agency/integrations/docezy';
import { clearForeignSessionLogoutFlag } from '@/utils/cross-tab-session';
import { clearAllFilterLocks } from '@/utils/filterLockPersist';

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    isAuthenticated: false,
    isUserValidate: false,
    isLoading: false,
    isSingleTenantRole: false,
    loggedInUserProfile: {} as Profile,
    userProfile: {} as Profile,
    refUserProfile: {} as Profile,
    isAgencyDetailsLoading: false,
    isAgencyAreasLoading: false,
    userTenants: [] as Array<IAuthTenants>,
    user: JSON.parse(localStorage.getItem('userCDetails') || '{}') as IUser,
    tenantDetails: {
      organisation: null as number | null,
      role: null as number | null
    },
    agencyNotificationSettings: [] as INotificationSettings[],
    agencyAreas: [] as IArea[],
    refAgencyAreas: [] as IArea[],
    emailForReset: '',
    otpForReset: '',

    // Set from the login form's checkbox before validateUserCredential/login runs. Survives SPA
    // navigation to /select-agency since it's Pinia state, not a component-local ref. Deliberately
    // NOT cleared by reset() (shared with changeTenant) so switching tenants mid-session preserves
    // the original remember-me choice; only a full logout resets it for the next fresh login.
    rememberMe: false as boolean,
    query: {
      token: null as string | null,
      tenant: null as string | null,
      e: null as string | null,
      s: null as string | null
    },

    // Reactive mirrors of localStorage values so Navbar getters stay live after login
    activeTenantData: JSON.parse(localStorage.getItem('userTenant') || 'null') as ITenant | null,
    activeRoleName: localStorage.getItem('userRole') || '',
    activeTenantSlug: localStorage.getItem('tenantSlug') || ''
  }),
  getters: {
    getLoggedInUserProfile(): Profile {
      if (this.loggedInUserProfile?.id) {
        return this.loggedInUserProfile;
      }

      return JSON.parse(localStorage.getItem('userData') || '{}');
    },

    getUserId(): number {
      const id: number | undefined =
        this.loggedInUserProfile?.id ?? (JSON.parse(localStorage.getItem('userData') || '{}') as { id?: number }).id;

      return Number(id);
    },

    getUserCDetails() {
      return JSON.parse(localStorage.getItem('userCDetails') || '{}');
    },

    getTenants() {
      const data: IAuthTenants[] = this.userTenants.length
        ? this.userTenants
        : JSON.parse(localStorage.getItem('tenants') || '[]');

      return data;
    },

    getCurrentTenantSlug(): string | null {
      return this.activeTenantSlug || localStorage.getItem('tenantSlug');
    },

    getCurrentTenantName(): string {
      if (this.activeTenantData?.name) {
        return this.activeTenantData.name;
      }

      return JSON.parse(localStorage.getItem('userTenant') || '{}')?.name || '';
    },

    getCurrentTenantRole(): string | null {
      return this.activeRoleName || localStorage.getItem('userRole');
    },

    getFormattedTenants() {
      const data: IAuthTenants[] = this.getTenants;

      const formatData = data?.flatMap((tenant: IAuthTenants) =>
        tenant.roles.map(role => ({
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          roleId: role.id,
          roleName: role.name,
          roleSlug: role.slug
        }))
      );

      return formatData as IFormatAuthTenants[];
    },

    getNotificationSettings: state => state.agencyNotificationSettings
  },
  actions: {
    // Reset
    clearTokens() {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },

    reset() {
      localStorage.removeItem('userData');
      localStorage.removeItem('userTenant');
      localStorage.removeItem('parentTenant');
      localStorage.removeItem('tenantSlug');
      localStorage.removeItem('userRole');
      localStorage.removeItem('integrations');
      localStorage.removeItem('agencyProfile');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('dateFormat');
      localStorage.removeItem('timeFormat');
      clearAllFilterLocks();
      this.tenantDetails = {
        organisation: null,
        role: null
      };
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      this.loggedInUserProfile = {} as Profile;
      this.activeTenantData = null;
      this.activeRoleName = '';
      this.activeTenantSlug = '';
    },

    async logout() {
      disconnectSocket();

      // Revoke the refresh token server-side before clearing local storage, so this request still
      // carries a valid Authorization header. Best-effort: local session must still clear even if
      // this call fails (e.g. already offline, or the token was already expired/revoked).
      try {
        await authService.logout();
      } catch (error) {
        console.log(error);
      }

      this.reset();

      localStorage.removeItem('tenants');
      localStorage.removeItem('userCDetails');
      this.isUserValidate = false;
      this.isAuthenticated = false;
      this.isSingleTenantRole = false;
      this.rememberMe = false;

      // Full navigation (not router.push) so any keep-alive'd component instances and the
      // running JS bundle are torn down — otherwise a build shipped mid-session keeps showing
      // stale UI until the user manually hard-reloads.
      window.location.href = '/';
    },

    // Reset

    async validateUserCredential(payload: IAuth) {
      const { data } = await authService.validateUserCredential(payload);
      const { user, tenants } = data;

      this.userTenants = tenants;
      this.user = user;

      localStorage.setItem('userCDetails', JSON.stringify(user));
      localStorage.setItem('tenants', JSON.stringify(tenants));

      if (tenants.length === 1 && tenants[0].roles.length === 1) {
        this.isSingleTenantRole = true;
      } else {
        this.isUserValidate = true;
      }
    },

    async login(payload: ILogin) {
      try {
        const { data } = await authService.login({ ...payload, rememberMe: this.rememberMe });
        const { accessToken, refreshToken } = data;

        clearForeignSessionLogoutFlag();

        localStorage.setItem('accessToken', accessToken);
        connectSocket(accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        const tenants = this.getTenants;

        const tenant = tenants?.find((t: { id: number }) => t.id === payload.tenantId);

        if (tenant) {
          localStorage.setItem('tenantSlug', tenant?.slug);
          this.activeTenantSlug = tenant.slug;
        }

        this.isAuthenticated = true;
      } catch (error) {
        this.isLoading = false;
        console.log(error);
      }
    },

    async fetchLoggedInUserProfile() {
      try {
        const { data } = await authService.getUserProfile();

        this.loggedInUserProfile = data.profile;
        this.activeTenantData = data.tenant;
        this.activeRoleName = data.profile.role?.name || '';

        localStorage.setItem('userRole', data.profile.role?.name);
        localStorage.setItem('userData', JSON.stringify(data.profile));
        localStorage.setItem('userTenant', JSON.stringify(data.tenant));

        // Fire-and-forget: load Docezy status so banner/settings show current state
        useDocezyStore()
          .fetchStatus()
          .catch(() => {});
      } catch (error) {
        this.isLoading = false;
        console.log(error);
        this.logout();
      }
    },

    async changeTenant(tenantId: number, roleId: number) {
      const { id } = JSON.parse(JSON.stringify(this.getUserCDetails));
      const { userId } = JSON.parse(JSON.stringify(this.getLoggedInUserProfile));

      await nextTick();

      // End the current agency/role session server-side before switching (each login is its own
      // session); best-effort so a failure here never blocks the switch.
      try {
        await authService.logout();
      } catch (error) {
        console.log(error);
      }

      this.reset();

      const payload = {
        tenantId,
        tenantRoleId: roleId,
        userId: Number(id),
        userUuid: userId
      };

      await this.login(payload);

      setTimeout(async () => {
        await this.fetchLoggedInUserProfile();

        await nextTick();

        await router.push('/agency/home');

        await nextTick();

        window.location.reload();
      }, 500);
    },

    async requestReset(email: string) {
      const response = await authService.requestReset(email);

      toast.success(response.data.message);

      this.emailForReset = email;
    },

    async verifyOtp(payload: { otp: string }) {
      const email = this.emailForReset;

      await authService.verifyOtp({
        email,
        otp: payload.otp
      });

      this.otpForReset = payload.otp;

      toast.success('OTP verified successfully!');
      router.push('/forgot-password/reset');

      return true;
    },

    async resetPassword(payload: IResetPassword) {
      const email = this.emailForReset;
      const otp = this.otpForReset;

      const fullPayload = {
        email,
        otp,
        password: payload.password,
        confirmPassword: payload.confirmPassword
      };

      await authService.resetPassword(fullPayload);

      toast.success('Password reset successfully!');
      router.push('/');
    },

    async signup(payload: ISignup) {
      await authService.signup(payload);

      toast.success('Your account has been successfully created!');

      return true;
    },

    async changePassword(payload: IChangePassword) {
      await authService.changePassword(payload);

      toast.success('Password changed successfully!');
    },

    async fetchAgencyDetails(hideLoader = false) {
      this.isAgencyDetailsLoading = true;

      try {
        const id = this.getUserId;
        const { data } = await authService.fetchAgencyDetails(id, hideLoader);

        this.userProfile = data;
        this.refUserProfile = JSON.parse(JSON.stringify(data));

        // Update: user data with image only
        const userData = localStorage.getItem('userData');
        if (userData) {
          const userDataObj = JSON.parse(userData);

          localStorage.setItem('userData', JSON.stringify({ ...userDataObj, image: data.image }));
        }
      } finally {
        this.isAgencyDetailsLoading = false;
      }
    },

    async resetAgencyDetails() {
      this.userProfile = JSON.parse(JSON.stringify(this.refUserProfile));
    },

    async updateAgencyDetails(payload: Partial<Profile>) {
      const id = this.getUserId;

      await authService.updateAgencyDetails(id, payload);

      toast.success('Profile updated successfully!');

      await Promise.all([this.fetchAgencyDetails(), this.fetchLoggedInUserProfile()]);

      return true;
    },

    async fetchAgencyNotificationSettings() {
      const id = this.getUserId;
      const { data } = await authService.fetchAgencyNotificationSettings(id);

      this.agencyNotificationSettings = data;
    },

    async updateAgencyNotificationSettings(payload: Partial<INotificationSettings>) {
      await authService.updateAgencyNotificationSettings(payload);

      this.fetchAgencyNotificationSettings();

      toast.success('Notification setting updated successfully!');
    },

    async fetchAgencyAreas(hideLoader = false) {
      this.isAgencyAreasLoading = true;

      try {
        const id = this.getUserId;

        const { data } = await authService.fetchAgencyAreas(id, hideLoader);

        this.agencyAreas = data;
        this.refAgencyAreas = JSON.parse(JSON.stringify(data));
      } finally {
        this.isAgencyAreasLoading = false;
      }
    },

    async resetAgencyAreas() {
      this.agencyAreas = JSON.parse(JSON.stringify(this.refAgencyAreas));
    },

    async updateAgencyAreas(payload: IUpdateAreas) {
      const id = this.getUserId;

      await authService.updateAgencyAreas(id, payload);

      this.fetchAgencyAreas();

      toast.success('Areas updated successfully!');
    },

    async emailVerification() {
      await authService.emailVerification(this.query);
    },

    async resendVerification(payload: IResendVerification) {
      await authService.resendVerification(payload);

      toast.success('Email resent successfully!');
    },

    async fetchUserTenantRoles(userId: number) {
      const { data } = await authService.fetchUserTenantRoles(userId);

      this.userTenants = data;

      localStorage.setItem('tenants', JSON.stringify(data));
    }
  }
});
